import { FieldInstance, FieldInstanceForResolver, Image, Revision, UserForResolver } from "db/definitions";
import { createRevisionForBatchResolver, putRevisions } from "db/queries/revisionQueries";
import { useEffect, useRef, useState } from "react";
import updateSingleFieldInstance from "utils/_updateSingleFieldInstance";
import { recordSentry } from "utils/recordSentry";
import { forgetField, markWritePending, reportWriteResult } from "utils/connectionStatus";
import { updateFieldInstance as updateFI } from "../db/queries/fieldInstanceQueries";
import useStateRef from "./useStateRef";
import { useAnalytics } from "./useAnalytics";

interface HandleChangeEvent {
	target: {
		name?: string;
		value: string;
	};
}

interface HandleImageChangeEvent {
	target: {
		name: "image";
		value: Image;
	};
}

export type HandleChangeType = HandleChangeEvent | HandleImageChangeEvent;

/**
 * Hook to manage inputs and save data to the cloud. Meant to be used within FieldAction.
 * @param onSubmit callback to be called after submit process
 * @param initialFieldInstance the initial or "saved" instance
 * @param online is online or not
 * @param userDoc for the current user
 */
function useField(
	onSubmit: ({ updatedField }: { updatedField: FieldInstance }) => Promise<void>,
	initialFieldInstance: FieldInstance,
	online: boolean,
	userDoc: UserForResolver,
	setupChanged: (changed: boolean) => void,
) {
	const [savedFieldInstance, setSavedFieldInstance, savedFieldInstanceRef] = useStateRef(initialFieldInstance);
	const [currentFieldInstance, setCurrentFieldInstance] = useState(initialFieldInstance);
	const [editing, setEditing] = useState(initialFieldInstance.editing === userDoc.username);
	const [activeEditing, setActiveEditing] = useState(false);
	const activeEditingRef = useRef(false);
	const onlineRef = useRef(online);
	// The latest value and the latest save function, readable from callbacks created in an earlier render (the
	// automatic resubmit after a failed write) without going stale.
	const currentFieldInstanceRef = useRef(currentFieldInstance);
	const handleSubmitRef = useRef<(newValue?: string) => Promise<boolean>>(async () => false);
	// Header of the field whose last save failed; FieldAction shows it to the user. Cleared by the next successful write.
	const [saveError, setSaveError] = useState<string | null>(null);

	useEffect(() => {
		currentFieldInstanceRef.current = currentFieldInstance;
	}, [currentFieldInstance]);

	/**
	 * Writes the field's latest value again after a failed write. Registered with connectionStatus, which calls it with
	 * backoff and again when the browser reports it is back online. The value lives in React state until it succeeds.
	 */
	function resubmitCurrentValue(): Promise<boolean> {
		return handleSubmitRef.current(currentFieldInstanceRef.current.value);
	}

	const { trackUserBehaviorEvent } = useAnalytics({
		userId: userDoc?.id || "",
		groupId: userDoc?.group || "",
		online,
	});

	// Keep activeEditingRef in sync so unmount cleanup and the 30s timer can read the
	// latest value without stale closure issues.
	useEffect(() => {
		activeEditingRef.current = activeEditing;
	}, [activeEditing]);

	// Keep onlineRef in sync so the unmount cleanup can read the current online state
	// without stale closure issues (empty-dep cleanups capture the mount-time value).
	useEffect(() => {
		onlineRef.current = online;
	}, [online]);

	// Auto-submit after 30 s of inactivity. Fires when the value differs OR when the
	// field is still flagged as being edited (catches the "typed then deleted" case).
	useEffect(() => {
		const timeOutId = setTimeout(() => {
			if (currentFieldInstance?.fieldTemplate?.inputType !== "image" && currentFieldInstance?.fieldTemplate?.inputType !== "table") {
				if (currentFieldInstance.value !== savedFieldInstance.value) {
					handleSubmit(currentFieldInstance.value);
				} else if (activeEditingRef.current) {
					// Value is unchanged but the field is still marked as being edited –
					// clear the editing flag without creating a revision.
					handleSubmit(currentFieldInstance.value);
				}
			}
		}, 30000);

		return () => clearTimeout(timeOutId);
	}, [currentFieldInstance.value, savedFieldInstance.value]);

	// Unmount cleanup — if the user navigates away while actively editing, clear the
	// editing lock in the DB so other users aren't blocked.
	useEffect(() => {
		return () => {
			// Once unmounted this field can no longer resubmit a failed write, so stop counting it as unsaved work.
			forgetField(savedFieldInstanceRef.current.id);
			if (!activeEditingRef.current) return;
			if (!onlineRef.current) return;
			const fi = savedFieldInstanceRef.current;
			const clearPayload: Pick<FieldInstanceForResolver, "id" | "formGroup" | "group"> & Partial<FieldInstanceForResolver> = {
				id: fi.id,
				formGroup: fi.formGroup,
				group: fi.group,
				editing: "",
				updatedAt: new Date().toISOString(),
			};
			// Fire-and-forget — component is unmounting so we can't await
			updateSingleFieldInstance(clearPayload).catch((error) => {
				recordSentry({ error, msg: `Failed to clear editing lock on unmount for field ${fi.id}` });
			});
		};
	}, []);

	/**
	 * just need to toggle active editing on focus instead of onchange.. There is some jank there
	 */
	function handleFocus() {
		activeEditingRef.current = true;
		setActiveEditing(true);
	}

	/**
	 * Change handler for field types
	 * @param e event data
	 * @param submitCb callback that will be called with updated value after processes
	 */
	const handleChange = (e: HandleChangeType, submitCb: (newValue: string) => void) => {
		const { target } = e;
		setupChanged(true);
		if (target.name === "image" && typeof target.value !== "string") {
			// its an image doc, which should already be updated in the db so just have to replace in
			// state and thats it.
			// this part is set because the event object must have name image, so the other editor
			// piece uses the main field instance value so shouldn't hit this at all..
			const fieldCopy: FieldInstance = { ...currentFieldInstance };
			fieldCopy.image = target.value;

			const updatedField: FieldInstanceForResolver = {
				id: fieldCopy.id,
				formInstance: fieldCopy.formInstance,
				fieldTemplateId: fieldCopy.fieldTemplateId,
				formGroup: fieldCopy.formGroup,
				group: fieldCopy.group,
				authors: fieldCopy.authors,
				lastAuthor: userDoc.username,
				value: fieldCopy.value,
				imageId: fieldCopy.imageId,
				editing: "",
				altInstanceId: fieldCopy.altInstanceId,
				fieldsOrder: fieldCopy.fieldsOrder,
				notes: fieldCopy.notes,
				createdAt: fieldCopy.createdAt,
				updatedAt: new Date().toISOString(),
			};
			if (online) {
				markWritePending(fieldCopy.id);
				updateSingleFieldInstance(updatedField)
					.then((ok) => reportWriteResult(fieldCopy.id, ok, resubmitCurrentValue))
					.catch((error) => {
						recordSentry({ error, msg: `Error updating field ${fieldCopy.id} image ${fieldCopy.imageId}` });
						reportWriteResult(fieldCopy.id, false, resubmitCurrentValue);
					});
			} else {
				updatedField.touched = 1;
				updateFI(updatedField);
			}

			setSavedFieldInstance(fieldCopy);
			setCurrentFieldInstance(fieldCopy);
			return;
		}
		if (typeof target.value === "string") {
			// Only write the editing lock to the DB on the first keystroke of an edit session.
			// Subsequent keystrokes omit the `editing` field from the update payload so that
			// multiple in-flight "editing: username" writes can't race against handleSubmit's
			// single "editing: """ clear and re-lock the field after it's been released.
			const isFirstChange = !activeEditing;
			let updated: FieldInstance = { ...currentFieldInstance, value: target.value };
			const timestamp = new Date().toISOString();

			updated.updatedAt = timestamp;

			// This is where the conditional to check if the field is being edited by the user yet was made before --- please leave this for now
			updated.editing = userDoc.username;
			const updatedFieldBase = {
				id: updated.id,
				fieldTemplateId: updated.fieldTemplateId,
				group: updated.group,
				formGroup: updated.formGroup,
				authors: updated.authors,
				lastAuthor: userDoc.username,
				value: updated.value,
				createdAt: updated.createdAt,
				updatedAt: timestamp,
				fieldsOrder: updated.fieldsOrder,
				notes: updated.notes,
			};
			// On the first keystroke, include `editing` so other users see the lock.
			// On subsequent keystrokes, omit it entirely so these fire-and-forget writes
			// cannot race against handleSubmit's awaited "editing: ''" clear.
			const updatedField: FieldInstanceForResolver = isFirstChange
				? { ...updatedFieldBase, editing: userDoc.username }
				: (updatedFieldBase as FieldInstanceForResolver);

			// These properties throw a fit in the batch resolver if they're undefined, and while this isn't the resolver, why temp fate?
			if (updated.altInstanceId) updatedField.altInstanceId = updated.altInstanceId;
			if (updated.formInstance) updatedField.formInstance = updated.formInstance;
			if (updated.imageId) updatedField.imageId = updated.imageId;
			if (online) {
				// Optimistic per-keystroke write. It stays fire-and-forget, but its outcome is now tracked: a failure keeps
				// the folder badge on "Unsaved Changes" and queues this field for an automatic resubmit of its latest value.
				markWritePending(updated.id);
				updateSingleFieldInstance(updatedField)
					.then((ok) => reportWriteResult(updated.id, ok, resubmitCurrentValue))
					.catch((error) => {
						recordSentry({ error, msg: `Error updating field ${updated.id}` });
						reportWriteResult(updated.id, false, resubmitCurrentValue);
					});
			} else {
				updatedField.touched = 1;
				updatedField.updatedAt = new Date().toISOString();
				updateFI(updatedField);
			}
			// This was the end of the conditional for if a user was just starting to edit the field.

			if (!editing) {
				setEditing(true);
			}

			if (!activeEditing) {
				activeEditingRef.current = true;
				setActiveEditing(true);
			}
			setCurrentFieldInstance(updated);
			// for field types that need to submit on change they can pass in submitCb for custom submission fun
			if (submitCb) {
				submitCb(target.value);
			}
		}
	};

	/**
	 * This handles receiving the updateObj data from the cloud and modifying the client state
	 */
	function handleUpdate(updateObj: FieldInstance) {
		const deepClone = structuredClone(updateObj);
		setSavedFieldInstance(deepClone);
		setCurrentFieldInstance(deepClone);
	}

	/**
	 * Submit callback, when a field is submitted - can be used in different places but will either update with
	 * the provided newValue or use the current state for the field and submit.
	 * @param newValue the value to be submitted (optional)
	 */
	async function handleSubmit(newValue?: string): Promise<boolean> {
		const savedFieldInstanceCopy = JSON.parse(JSON.stringify(savedFieldInstance));
		// Below needs to be a Nullish coalescing operator rather than logical OR operator for checkboxes
		const updatedValue = newValue ?? currentFieldInstance.value;

		if (updatedValue === savedFieldInstanceCopy.value) {
			// No change since last update - but need to remove the user anyways
			// also for now going to push on the user name to authors after checking if its in there already.
			setupChanged(false);
		}

		// also for now going to push on the user name to authors after checking if its in there already.
		// reset editing to an empty string to "remove the user from the field"
		const updatedField: FieldInstanceForResolver = {
			id: savedFieldInstanceCopy.id,
			formInstance: savedFieldInstanceCopy.formInstance,
			fieldTemplateId: savedFieldInstanceCopy.fieldTemplateId,
			altInstanceId: savedFieldInstanceCopy.altInstanceId,
			formGroup: savedFieldInstanceCopy.formGroup,
			group: savedFieldInstanceCopy.group,
			authors: savedFieldInstanceCopy.authors,
			lastAuthor: userDoc.username,
			editing: "",
			createdAt: savedFieldInstanceCopy.createdAt,
			updatedAt: new Date().toISOString(),
			value: updatedValue,
			fieldsOrder: savedFieldInstanceCopy.fieldsOrder,
			notes: savedFieldInstanceCopy.notes,
		};
		if (!updatedField.authors.includes(userDoc.username)) updatedField.authors.push(userDoc.username);
		const newFieldInstance: FieldInstance = {
			...updatedField,
			fieldTemplate: currentFieldInstance.fieldTemplate,
			tableFields: currentFieldInstance.tableFields,
		};

		const updatedSizeInBytes = new TextEncoder().encode(JSON.stringify(updatedField)).length;

		if (updatedSizeInBytes > 400 * 1024) {
			recordSentry({
				error: { data: updatedField.id, error: "update request object is too large for request" },
				msg: "FieldInstance object is over 400kb",
			});
		}
		// This is here to ensure that the revision is not created if the value is the same as the savedFieldInstance
		if (savedFieldInstanceCopy.value !== updatedValue) {
			// add the revision to the db (originally this was within the updateFI and after this next query but I decided
			// it actually makes sense to have the revision before, then if something fails with the update, the revision
			// could be there to help recover.
			const newRevisionData: Pick<Revision, "author" | "fieldInstance" | "formGroup" | "group" | "value"> & Partial<Revision> = {
				fieldInstance: savedFieldInstanceCopy.id,
				value: updatedValue,
				group: savedFieldInstanceCopy.group,
				formGroup: savedFieldInstanceCopy.formGroup,
				author: userDoc.username,
			};
			const newRevision = createRevisionForBatchResolver(newRevisionData);
			putRevisions([newRevision], online);
		}

		// Fire-and-forget: analytics must not block or abort the critical save path.
		trackUserBehaviorEvent("field_edit", {
			fieldId: savedFieldInstanceCopy.id,
			formGroup: savedFieldInstanceCopy.formGroup,
			timestamp: new Date().toISOString(),
		}).catch(() => {
			/* analytics failure is non-fatal — save continues regardless */
		});

		if (online) {
			markWritePending(updatedField.id);
			let ok = false;
			try {
				ok = await updateSingleFieldInstance(updatedField);
			} catch (error) {
				recordSentry({ error, msg: "Error updating field in handleSubmit: " });
				ok = false;
			}
			// Whether or not the write landed, the user is no longer actively typing in this field.
			activeEditingRef.current = false;
			setActiveEditing(false);
			if (ok) {
				reportWriteResult(updatedField.id, true);
				setSavedFieldInstance(newFieldInstance);
				setCurrentFieldInstance(newFieldInstance);
				setSaveError(null);
				setupChanged(false);
				await onSubmit({ updatedField: newFieldInstance });
				return true;
			}
			// The value was not written. Keep it in state (the user still sees it), keep the folder marked unsaved, tell the
			// user, and queue an automatic resubmit. No partial "editing: ''" write is sent: its subscription echo would
			// carry the stored (old) value with a newer timestamp and revert the field on screen.
			reportWriteResult(updatedField.id, false, resubmitCurrentValue);
			setupChanged(true);
			setSaveError(currentFieldInstance.fieldTemplate?.header || "This field");
			return false;
		} else {
			updatedField.touched = 1;
			const status = await updateFI(updatedField);
			if (status) {
				setSavedFieldInstance(newFieldInstance);
				setCurrentFieldInstance(newFieldInstance);
				activeEditingRef.current = false;
				setActiveEditing(false);
				setupChanged(false);
				onSubmit({ updatedField: newFieldInstance });
			}
			return !!status;
		}
	}
	handleSubmitRef.current = handleSubmit;

	async function handleRefreshField() {
		// setupChanged called here because if hey click the refresh fiel d button, onBlur should have fired already if they were in
		// another field. This might be unecessary, but I also didn't want to put it at the end in case they click refresh and
		// immediately edit another field
		setupChanged(false);
		const updatedField: Pick<FieldInstanceForResolver, "id" | "formGroup" | "group"> & Partial<FieldInstanceForResolver> = {
			id: currentFieldInstance.id,
			editing: "",
			updatedAt: new Date().toISOString(),
			group: currentFieldInstance.group,
			formGroup: currentFieldInstance.formGroup,
		};
		if (online) {
			await updateSingleFieldInstance(updatedField);
		} else {
			await updateFI(updatedField);
		}
		const finalField: FieldInstance = { ...currentFieldInstance, ...updatedField };
		setCurrentFieldInstance(finalField);
		setSavedFieldInstance(finalField);
		activeEditingRef.current = false;
		setEditing(false);
		setActiveEditing(false);
	}

	/**
	 * This is the click and submit flow, which is used for fields that need to submit on click, like picklists.
	 * It is slightly different in that it does not put a user into the field at all and then manages some of the regular handleChange
	 * functionality, as well as the submit process.
	 * @param e event data
	 */
	async function handleClickAndSubmit(e: HandleChangeType) {
		// the click and submit flow!!
		const { target } = e;
		const savedFieldInstanceCopy = JSON.parse(JSON.stringify(savedFieldInstance));
		if (typeof target.value === "string") {
			let updated: FieldInstance = { ...currentFieldInstance, value: target.value };
			const timestamp = new Date().toISOString();

			updated.updatedAt = timestamp;

			// This is where the conditional to check if the field is being edited by the user yet was made before --- please leave this for now
			updated.editing = userDoc.username;
			const updatedField: FieldInstanceForResolver = {
				id: updated.id,
				fieldTemplateId: updated.fieldTemplateId,
				group: updated.group,
				formGroup: updated.formGroup,
				authors: updated.authors,
				lastAuthor: userDoc.username,
				value: updated.value,
				editing: "",
				createdAt: updated.createdAt,
				updatedAt: timestamp,
				fieldsOrder: updated.fieldsOrder,
				notes: updated.notes,
			};

			// These properties throw a fit in the batch resolver if they're undefined, and while this isn't the resolver, why temp fate?
			if (updated.altInstanceId) updatedField.altInstanceId = updated.altInstanceId;
			if (updated.formInstance) updatedField.formInstance = updated.formInstance;
			if (updated.imageId) updatedField.imageId = updated.imageId;

			// Update state immediately so the UI reflects the change without waiting for network calls.
			if (!editing) setEditing(true);
			if (!activeEditing) {
				activeEditingRef.current = true;
				setActiveEditing(true);
			}
			setSavedFieldInstance(updated);
			setCurrentFieldInstance(updated);
			onSubmit({ updatedField: updated });

			if (savedFieldInstanceCopy.value !== updated.value) {
				const newRevisionData: Pick<Revision, "author" | "fieldInstance" | "formGroup" | "group" | "value"> & Partial<Revision> = {
					fieldInstance: savedFieldInstanceCopy.id,
					value: updated.value,
					group: savedFieldInstanceCopy.group,
					formGroup: savedFieldInstanceCopy.formGroup,
					author: userDoc.username,
				};
				const newRevision = createRevisionForBatchResolver(newRevisionData);
				putRevisions([newRevision], online);
			}

			// Analytics and DB writes are fire-and-forget after the UI has already updated.
			trackUserBehaviorEvent("field_edit", {
				fieldId: savedFieldInstanceCopy.id,
				formGroup: savedFieldInstanceCopy.formGroup,
				timestamp: new Date().toISOString(),
			}).catch(() => {});

			if (online) {
				markWritePending(updated.id);
				updateSingleFieldInstance(updatedField)
					.then((ok) => {
						reportWriteResult(updated.id, ok, resubmitCurrentValue);
						if (!ok) {
							setupChanged(true);
							setSaveError(updated.fieldTemplate?.header || "This field");
						} else {
							setSaveError(null);
						}
					})
					.catch((error) => {
						recordSentry({ error, msg: `Error updating field ${updated.id}` });
						reportWriteResult(updated.id, false, resubmitCurrentValue);
						setupChanged(true);
						setSaveError(updated.fieldTemplate?.header || "This field");
					});
			} else {
				updatedField.touched = 1;
				updatedField.updatedAt = new Date().toISOString();
				updateFI(updatedField);
			}
		}
	}

	function clearSaveError() {
		setSaveError(null);
	}

	const results = {
		activeEditing,
		currentFieldInstance,
		editing,
		handleChange,
		handleFocus,
		handleRefreshField,
		handleClickAndSubmit,
		handleSubmit,
		handleUpdate,
		saveError,
		clearSaveError,
	};

	return results;
}

export default useField;
